import React, { createContext, useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { collection, query, where, getDocs, doc, deleteDoc, Timestamp, writeBatch, arrayUnion } from "firebase/firestore";
import { db, auth } from "../../../../config/firebaseConfig";
import { calculateEnrolmentDetails } from "../../constants/ai-lab-constants";
import { CheckCircle, XCircle, RotateCcw } from "lucide-react";
import { updateUserDetails } from "../../../../redux/features/authSlice";

export const AiLabEnrolmentContext = createContext(null);

const AiLab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userDetails: parentsDetails } = useSelector((state) => state.auth);
  const parentId = parentsDetails?.id || auth?.currentUser?.uid;

  // Global wizard state
  const [numChildren, setNumChildren] = useState(1);
  const [childrenData, setChildrenData] = useState([
    { name: "", school: "", grade: "", interests: [], schedule: {} }
  ]);
  const [otherQueries, setOtherQueries] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);

  // Firestore status states
  const [pendingEnrolment, setPendingEnrolment] = useState(null);
  const [enrolmentId, setEnrolmentId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("idle");

  // Keep state sync'd when numChildren adjusts
  useEffect(() => {
    if (parentsDetails?.accountType === "student") {
      setNumChildren(1);
      return;
    }
    setChildrenData((prev) => {
      const copy = [...prev];
      if (copy.length < numChildren) {
        const diff = numChildren - copy.length;
        for (let i = 0; i < diff; i++) {
          copy.push({ name: "", school: "", grade: "", interests: [], schedule: {} });
        }
      } else if (copy.length > numChildren) {
        return copy.slice(0, numChildren);
      }
      return copy;
    });
  }, [numChildren, parentsDetails]);

  // Pre-fill child details on mount/load if child user
  useEffect(() => {
    if (parentsDetails?.accountType === "student") {
      setNumChildren(1);
      setChildrenData([{
        id: parentId,
        name: parentsDetails.name || "",
        school: parentsDetails.school || "",
        grade: parentsDetails.grade || "",
        interests: [],
        schedule: {}
      }]);
    }
  }, [parentsDetails, parentId]);

  // Recalculate totals
  useEffect(() => {
    const details = calculateEnrolmentDetails(childrenData);
    setTotalAmount(details.finalTotal);
  }, [childrenData]);

  // Clear error and scroll to top on step transitions
  useEffect(() => {
    setError("");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  // Fetch pending enrollment on load
  useEffect(() => {
    const checkPending = async () => {
      if (!parentId) return;
      try {
        setLoading(true);
        if (parentsDetails?.accountType === "student") {
          const aiLab = parentsDetails?.enrolment?.["ai-lab"];
          if (aiLab && aiLab.paymentStatus === "pending") {
            const pendingCourses = Object.keys(aiLab.courses || {});
            setPendingEnrolment({
              id: parentId,
              children: [{
                id: parentId,
                name: parentsDetails.name || "",
                school: parentsDetails.school || "",
                grade: parentsDetails.grade || "",
                interests: pendingCourses,
                schedule: aiLab.courses || {}
              }],
              otherQueries: aiLab.otherQueries || "",
              totalAmount: calculateEnrolmentDetails([{ interests: pendingCourses }]).finalTotal
            });
            setEnrolmentId(parentId);
          }
        } else {
          // Parent user: Query children collection for pending enrolment
          const q = query(
            collection(db, "students"),
            where("parentUid", "==", parentId)
          );
          const snap = await getDocs(q);
          const pendingChildren = [];
          let pendingOtherQueries = "";
          snap.docs.forEach(doc => {
            const data = doc.data();
            const aiLab = data.enrolment?.["ai-lab"];
            if (aiLab && aiLab.paymentStatus === "pending") {
              const pendingCourses = Object.keys(aiLab.courses || {});
              pendingChildren.push({
                id: doc.id,
                name: data.name,
                school: data.school || "",
                grade: data.grade,
                interests: pendingCourses,
                schedule: aiLab.courses || {}
              });
              if (aiLab.otherQueries) {
                pendingOtherQueries = aiLab.otherQueries;
              }
            }
          });
          if (pendingChildren.length > 0) {
            setPendingEnrolment({
              id: parentId,
              children: pendingChildren,
              otherQueries: pendingOtherQueries,
              totalAmount: calculateEnrolmentDetails(pendingChildren).finalTotal
            });
            setEnrolmentId(parentId);
          }
        }
      } catch (err) {
        console.error("Error checking pending enrolments:", err);
      } finally {
        setLoading(false);
      }
    };
    checkPending();
  }, [parentId, parentsDetails]);

  // Check and retry failed payments in session storage
  useEffect(() => {
    const retryDataRaw = sessionStorage.getItem("ai-lab-payment-retryData");
    if (!retryDataRaw) return;
    try {
      const retryData = JSON.parse(retryDataRaw);
      if (retryData?.response && retryData?.order && retryData?.activeEnrolmentId) {
        (async () => {
          try {
            setStage("saving");
            setLoading(true);
            
            // Re-authenticate user to ensure token is valid
            if (auth.currentUser) {
              await auth.currentUser.getIdToken(true).catch(() => {});
            }

            const paymentDocId = retryData.response.razorpay_payment_id;
            const batch = writeBatch(db);
            const paymentRef = doc(db, "payments", paymentDocId);
            batch.set(paymentRef, {
              orderId: retryData.response.razorpay_order_id,
              amount: retryData.order.amount,
              currency: retryData.order.currency,
              paymentId: retryData.response.razorpay_payment_id,
              paymentSignature: retryData.response.razorpay_signature,
              uid: auth.currentUser?.uid || parentId || "",
              userId: parentsDetails?.id || parentId || "",
              status: "verified",
              paymentAt: Timestamp.fromDate(new Date()),
              enrolmentId: retryData.activeEnrolmentId,
              type: "ai-lab"
            }, { merge: true });

            // Update children docs
            if (retryData.accountType === "child" || retryData.accountType === "student") {
               const childDocRef = doc(db, "students", parentId);
               batch.update(childDocRef, {
                 "enrolment.ai-lab.paymentStatus": "verified",
                 "enrolment.ai-lab.paymentRef": paymentRef.id,
                 "enrolment.ai-lab.paymentId": paymentDocId,
                 "enrolment.ai-lab.paymentAt": Timestamp.fromDate(new Date()),
               });
             } else if (retryData.childrenData) {
               retryData.childrenData.forEach(c => {
                 const childDocRef = doc(db, "students", c.id);
                 batch.update(childDocRef, {
                   "enrolment.ai-lab.paymentStatus": "verified",
                   "enrolment.ai-lab.paymentRef": paymentRef.id,
                   "enrolment.ai-lab.paymentId": paymentDocId,
                   "enrolment.ai-lab.paymentAt": Timestamp.fromDate(new Date()),
                 });
               });
             }


            await batch.commit();

            if ((retryData.accountType === "child" || retryData.accountType === "student") && retryData.childrenData?.[0]) {
              const updatedEnrolment = {
                ...parentsDetails?.enrolment,
                "ai-lab": {
                  courses: retryData.childrenData[0].interests.reduce((acc, courseId) => {
                    acc[courseId] = retryData.childrenData[0].schedule[courseId] || {};
                    return acc;
                  }, {}),
                  paymentStatus: "verified",
                  paymentRef: paymentRef.id,
                  paymentId: paymentDocId,
                  paymentAt: new Date().toISOString()
                }
              };
              dispatch(updateUserDetails({ enrolment: updatedEnrolment }));
            }

            sessionStorage.removeItem("ai-lab-payment-retryData");
            setPaymentSuccess(true);
            setPaymentFailed(false);
          } catch (e) {
            console.error("Session storage recovery retry failed:", e);
          } finally {
            setLoading(false);
            setStage("idle");
          }
        })();
      }
    } catch (err) {
      console.error("Error restoring session retry details:", err);
    }
  }, [parentId, parentsDetails, dispatch]);

  const handleDiscardPending = async () => {
    if (!enrolmentId || !pendingEnrolment) return;
    try {
      setLoading(true);
      const batch = writeBatch(db);
      
      if (parentsDetails?.accountType === "student") {
        batch.update(doc(db, "students", parentId), {
          "enrolment.ai-lab": null
        });
      } else {
        pendingEnrolment.children.forEach(c => {
          if (c.id) {
            batch.update(doc(db, "students", c.id), {
              "enrolment.ai-lab": null
            });
          }
        });
      }
      
      await batch.commit();

      if (parentsDetails?.accountType === "student") {
        const updatedEnrolment = { ...parentsDetails.enrolment };
        delete updatedEnrolment["ai-lab"];
        dispatch(updateUserDetails({ enrolment: updatedEnrolment }));
      }

      setPendingEnrolment(null);
      setEnrolmentId(null);
      
      if (parentsDetails?.accountType === "student") {
        setChildrenData([{
          id: parentId,
          name: parentsDetails.name || "",
          school: parentsDetails.school || "",
          grade: parentsDetails.grade || "",
          interests: [],
          schedule: {}
        }]);
      } else {
        setNumChildren(1);
        setChildrenData([{ name: "", school: "", grade: "", interests: [], schedule: {} }]);
      }
      setOtherQueries("");
      navigate("/dashboard/ai-lab/enrol");
    } catch (err) {
      console.error(err);
      setError("Failed to discard pending session.");
    } finally {
      setLoading(false);
    }
  };

  const handleResumePending = () => {
    if (!pendingEnrolment) return;
    setNumChildren(pendingEnrolment.children.length);
    setChildrenData(pendingEnrolment.children.map(c => ({
      id: c.id,
      name: c.name,
      school: c.school || "",
      grade: c.grade,
      interests: c.interests || [],
      schedule: c.schedule || {}
    })));
    setOtherQueries(pendingEnrolment.otherQueries || "");
    setTotalAmount(pendingEnrolment.totalAmount || 0);
    setPendingEnrolment(null);
    navigate("/dashboard/ai-lab/enrol");
  };

  // --- Dynamic String course listings for receipt details ---
  const getSelectedCoursesString = () => {
    const coursesMap = childrenData.flatMap(c => c.interests);
    const uniqueCourses = [...new Set(coursesMap)];
    if (uniqueCourses.length === 0) return "AI & Robotics — Monthly Batch";
    
    // map labels
    const labels = uniqueCourses.map(id => {
      if (id === "ai-robotics") return "AI & Robotics";
      if (id === "coding") return "Coding";
      if (id === "design") return "Design";
      if (id === "video-editing") return "Video Editing";
      if (id === "aerodynamics") return "Aerodynamics";
      if (id === "sketching") return "Sketching & Drawing";
      if (id === "3d-modelling") return "3D Modelling & Printing";
      if (id === "smart-money") return "Smart Money Skills";
      return id;
    });
    return labels.join(", ") + " — Monthly Batch";
  };

  // --- Render success or failure directly in orchestrator for full-page layout ---
  if (paymentSuccess) {
    const referenceCode = enrolmentId ? `#PL-${enrolmentId.slice(-6).toUpperCase()}` : "#PL-000000";
    return (
      <div className="w-full mx-auto max-w-2xl px-4 py-12 text-left">
        <div className="bg-white rounded-3xl border border-gray-100 p-8 md:p-10 shadow-lg text-center space-y-8">
          
          {/* Header Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 text-green-500 mb-2">
            <CheckCircle className="w-12 h-12" />
          </div>

          {/* Heading */}
          <div className="space-y-3">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight leading-tight">
              Payment successful! Your child is enroled. 🎉
            </h2>
            <p className="text-gray-500 font-semibold text-sm">
              We have received your enrolment and verified the checkout details.
            </p>
          </div>

          <hr className="border-gray-100" />

          {/* Next steps list */}
          <div className="space-y-4 text-left">
            <h3 className="font-extrabold text-sm text-gray-800 uppercase tracking-wider">What happens next:</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <p className="text-xs sm:text-sm font-semibold text-gray-700 leading-relaxed">
                  We’ll WhatsApp you within 24 hours with your child’s class schedule and joining link.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <p className="text-xs sm:text-sm font-semibold text-gray-700 leading-relaxed">
                  Your first class is confirmed - your child will build something real in that very first session.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <p className="text-xs sm:text-sm font-semibold text-gray-700 leading-relaxed">
                  Check your email for your payment receipt and course details.
                </p>
              </div>
            </div>
          </div>

          {/* Receipt Details Card */}
          <div className="bg-gray-50 rounded-2xl p-5 text-left border border-gray-150 space-y-2.5 text-xs sm:text-sm">
            <div className="flex justify-between items-center text-gray-600">
              <span className="font-medium">Your order reference:</span>
              <span className="font-bold text-gray-900">{referenceCode}</span>
            </div>
            <div className="flex justify-between items-center text-gray-600">
              <span className="font-medium">Course:</span>
              <span className="font-bold text-gray-900 truncate max-w-[280px] sm:max-w-xs">{getSelectedCoursesString()}</span>
            </div>
            <div className="flex justify-between items-center text-gray-600">
              <span className="font-medium">Amount paid:</span>
              <span className="font-bold text-blue-600 text-base">₹{totalAmount.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between items-center text-gray-600">
              <span className="font-medium">Receipt sent to:</span>
              <span className="font-bold text-gray-900 truncate max-w-[180px] sm:max-w-xs">{parentsDetails?.email || auth?.currentUser?.email || "your email"}</span>
            </div>
          </div>

          {/* Contact footer */}
          <div className="bg-blue-50/40 rounded-xl p-4 border border-blue-100 text-xs sm:text-sm text-blue-800 font-bold flex items-center justify-center gap-2">
            <span>Questions? WhatsApp us:</span>
            <a href="https://wa.me/919560734660" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900 font-black">
              +91 9560734660
            </a>
          </div>

          <div className="pt-2">
            <button
              onClick={() => {
                sessionStorage.removeItem("ai-lab-payment-retryData");
                setPaymentSuccess(false);
                navigate("/dashboard");
              }}
              className="px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-xs font-bold transition shadow-sm"
            >
              Go to Dashboard
            </button>
          </div>

        </div>
      </div>
    );
  }

  if (paymentFailed) {
    return (
      <div className="w-full mx-auto max-w-xl px-4 py-12 text-left">
        <div className="bg-white rounded-3xl border border-red-100 p-8 shadow-lg text-center space-y-8">
          
          {/* Failure Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 text-red-500 mb-2">
            <XCircle className="w-12 h-12" />
          </div>

          {/* Error Message */}
          <div className="space-y-2">
            <h2 className="text-xl md:text-2xl font-black text-gray-900 leading-tight">
              Payment was not completed
            </h2>
            <p className="text-sm font-bold text-red-650 bg-red-50 py-1.5 px-3 rounded-full inline-block">
              Don’t worry — you have not been charged.
            </p>
          </div>

          {/* Body details */}
          <p className="text-xs sm:text-sm text-gray-500 leading-relaxed font-semibold">
            This sometimes happens due to a network issue or bank timeout. Please try again — it usually works on the second attempt.
          </p>

          {/* Retry CTA */}
          <div>
            <button
              onClick={() => {
                setPaymentFailed(false);
                setError("");
                navigate("/dashboard/ai-lab/enrol");
              }}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-750 text-white rounded-xl text-sm font-extrabold flex items-center justify-center gap-2 mx-auto shadow-sm transition"
            >
              <RotateCcw className="w-4 h-4" /> Try Again &rarr;
            </button>
          </div>

          <hr className="border-gray-100" />

          {/* Manual Instructions */}
          <div className="space-y-3">
            <p className="text-xs text-gray-500 font-semibold">
              If the problem continues, WhatsApp us directly and we’ll help you complete your enrolment manually:
            </p>
            <div className="inline-block bg-gray-50 rounded-xl px-4 py-2 border border-gray-150 text-xs sm:text-sm font-bold text-gray-700">
              <a href="https://wa.me/919560734660" target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
                💬 WhatsApp: <strong className="text-gray-950 font-black">+91 9560734660</strong>
              </a>
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <AiLabEnrolmentContext.Provider value={{
      numChildren, setNumChildren,
      childrenData, setChildrenData,
      otherQueries, setOtherQueries,
      totalAmount,
      enrolmentId, setEnrolmentId,
      paymentSuccess, setPaymentSuccess,
      paymentFailed, setPaymentFailed,
      pendingEnrolment, setPendingEnrolment,
      loading, setLoading,
      error, setError,
      stage, setStage,
      parentsDetails, parentId,
      handleDiscardPending, handleResumePending
    }}>
      <div className="w-full mx-auto max-w-7xl px-4 py-8">
        <div>
          <Outlet />
        </div>
      </div>
    </AiLabEnrolmentContext.Provider>
  );
};

export default AiLab;
